import styled from '@emotion/styled';
import { SxProps, Theme } from '@mui/material';
import MuiTab, { TabProps } from '@mui/material/Tab';
import React from 'react';
import { Link, useHistory } from 'react-router-dom';
import { bluePlanetTheme } from 'ui/theme';
import { quarterSpacing } from 'ui/theme/themeConstants';

interface Props {
  onClick?: () => void;
  value: string;
  label?: React.ReactNode;
  icon?: React.ReactElement;
  iconPosition?: TabProps['iconPosition'];
  hasActivityIndicator?: boolean;
  sx?: SxProps<Theme>;
  title?: string;
  href?: string;
  replace?: boolean;
}

const ActivityDot = styled.span`
  position: absolute;
  width: 6px;
  height: 6px;
  background-color: ${bluePlanetTheme.bluePlanetPalette.orange.main};
  border-radius: 50%;
  right: -8px;
  top: 0;
`;

function Label({ hasActivityIndicator, children }: { hasActivityIndicator?: boolean; children?: React.ReactNode }) {
  return hasActivityIndicator ? (
    <span style={{ position: 'relative' }}>
      {children}
      <ActivityDot />
    </span>
  ) : (
    <>{children}</>
  );
}

export default function Tab(props: Props) {
  const { value, hasActivityIndicator, href, replace, ...rest } = props;
  const label = props.label ?? value;
  const history = useHistory();

  // Handle spacebar navigation for href links
  const handleKeyDown = React.useCallback(
    (event: React.KeyboardEvent) => {
      if (event.key === ' ' && href) {
        event.preventDefault(); // Prevent default spacebar behavior (scrolling)
        if (replace) {
          history.replace(href);
        } else {
          history.push(href);
        }
      }
    },
    [href, replace, history],
  );

  // If href is provided, use Link component for navigation
  const tabProps = href
    ? {
        component: Link,
        to: href,
        replace,
        onKeyDown: handleKeyDown,
        tabIndex: 0, // Only apply tabIndex for href tabs to maintain focusability for links
      }
    : {};

  return (
    <MuiTab
      sx={{
        '.MuiTab-iconWrapper': {
          marginRight: props.iconPosition === 'start' ? quarterSpacing : undefined,
        },
      }}
      value={value}
      {...rest}
      {...tabProps}
      label={<Label hasActivityIndicator={hasActivityIndicator}>{label}</Label>}
    />
  );
}
